import React, { useCallback, useMemo, useEffect, useState } from "react";
import { TransportData, TripTransport } from "@/types/trip";
import { BookingStatusButton } from "@/components/common/BookingStatusButton";
import { DateTime } from "luxon";
import { formatPrice } from "@/lib/general";
import useTripStore from "@/stores/tripBuilder/useTrip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Tooltip } from "@heroui/tooltip";
import { CheckIcon } from "lucide-react";
import {
  ArrowRightCircleIcon,
  XMarkIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { calculateDuration, formatDuration } from "@/lib/transport";
import { BookingModal } from "@/components/common/BookingModal";

interface UserFlightProps {
  relevantAddedFlight: TripTransport;
  transportData: TransportData;
  onDeleteFlight: () => void;
  setFlightModalOpen: (isOpen: boolean) => void;
  handleStatusChange: (newStatus: string) => void;
  isUpdating: Record<string, boolean>;
}

const UserFlight: React.FC<UserFlightProps> = ({
  relevantAddedFlight,
  transportData,
  onDeleteFlight,
  setFlightModalOpen,
  handleStatusChange,
  isUpdating,
}) => {
  const { tripData } = useTripStore();
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [lastBookingAttemptTime, setLastBookingAttemptTime] = useState<
    number | null
  >(null);

  // Check if user returned from booking page
  useEffect(() => {
    const checkIfReturnedFromBooking = () => {
      if (lastBookingAttemptTime) {
        const timeElapsed = Date.now() - lastBookingAttemptTime;
        // If less than 15 minutes have passed since booking attempt
        if (timeElapsed < 15 * 60 * 1000) {
          setShowBookingConfirmation(true);
          setLastBookingAttemptTime(null);
        }
      }
    };

    // Check when window regains focus
    const handleFocus = () => {
      checkIfReturnedFromBooking();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastBookingAttemptTime]);

  const renderAirlineAvatars = useCallback(() => {
    if (!relevantAddedFlight?.legs) return [];

    const uniqueAirlines = Array.from(
      new Set(relevantAddedFlight.legs.map((leg) => leg.provider.code))
    );

    return uniqueAirlines.map((airline, index) => {
      const imageUrl = `https://images.kiwi.com/airlines/128x128/${airline}.png?default=airline.png`;
      return (
        <div
          key={airline}
          className="airline-avatar rounded-full border-2 border-white shadow-sm bg-white"
          style={{
            width: "2.5rem",
            height: "2.5rem",
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            marginLeft: index > 0 ? "-1rem" : "0",
            zIndex: uniqueAirlines.length - index,
          }}
        />
      );
    });
  }, [relevantAddedFlight?.legs]);

  const renderAirlineNames = useCallback(() => {
    if (!relevantAddedFlight?.legs) return "";

    const uniqueAirlines = new Set(
      relevantAddedFlight.legs.map((leg) => leg.provider.name)
    );
    return uniqueAirlines.size > 1
      ? "Multiple Airlines"
      : Array.from(uniqueAirlines).join(" + ");
  }, [relevantAddedFlight?.legs]);

  const handleBookNow = useCallback(() => {
    setShowBookingModal(true);
  }, []);

  const handleProviderBooked = () => {
    setLastBookingAttemptTime(Date.now());
    setShowBookingModal(false);
  };

  const handleConfirmBooking = () => {
    handleStatusChange("booked");
    setShowBookingConfirmation(false);
    toast({
      title: "Flight Booked",
      description: "Your flight has been marked as booked.",
    });
  };

  return (
    <>
      <div className="h-full flex flex-col justify-between">
        {/* Header - Fixed height */}
        <div className="h-[40px] flex items-center justify-between">
          <div className="flex items-center gap-2">
            {renderAirlineAvatars()}
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-neutral-900">
                  {renderAirlineNames()}
                </span>
                <span className="text-xs font-medium text-neutral-500">
                  {relevantAddedFlight.legs[0]?.provider.name}{" "}
                  {relevantAddedFlight.legs[0]?.reference}
                  {relevantAddedFlight.legs.length > 1 &&
                    ` +${relevantAddedFlight.legs.length - 1}`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <ClockIcon className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                <span>
                  {formatDuration(
                    relevantAddedFlight.legs.reduce(
                      (total, leg) => total + leg.duration,
                      0
                    )
                  )}
                </span>

                <span className="w-1 h-1 bg-neutral-300 rounded-full"></span>
                <span>
                  {relevantAddedFlight.legs.length === 1
                    ? "Direct"
                    : `${relevantAddedFlight.legs.length - 1} Stop${
                        relevantAddedFlight.legs.length > 2 ? "s" : ""
                      }`}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <BookingStatusButton
              status={relevantAddedFlight.bookingStatus || "not_booked"}
              onStatusChange={handleStatusChange}
              showStatus={true}
              compact={false}
              disabled={isUpdating[relevantAddedFlight.id]}
            />
            {/* <button
              className={`text-neutral-600 h-8 w-8 hover:bg-slate-100 hover:text-slate-700 duration-300 ease-in-out rounded-full`}
              onClick={() => setFlightModalOpen(true)}
            >
              <i className="fal fa-circle-info text-sm" />
            </button> */}
            <button
              className="text-neutral-600 h-8 w-8 hover:bg-rose-50 hover:text-rose-500 duration-300 ease-in-out rounded-full"
              onClick={onDeleteFlight}
            >
              <i className="fal fa-trash text-sm" />
            </button>
          </div>
        </div>

        {/* Timeline - Fixed height */}
        <div className="h-[80px] relative mt-2">
          <div className="flex justify-between items-center space-x-4">
            {/* Departure */}
            <div className="w-[6rem] pr-4">
              <div className="text-base font-bold text-neutral-900">
                {DateTime.fromISO(
                  relevantAddedFlight.legs[0]?.departure.time.utc || ""
                )
                  .setZone(transportData.locationAbove?.date.time_zone)
                  .toFormat("HH:mm")}
              </div>
              <div className="text-xs font-medium text-neutral-900 truncate">
                {relevantAddedFlight.legs[0]?.departure.location.name}
              </div>
              {relevantAddedFlight.legs[0]?.departure.location.code && (
                <div className="text-[10px] text-neutral-500 truncate">
                  {relevantAddedFlight.legs[0]?.departure.location.code ||
                    "N/A"}
                </div>
              )}
            </div>

            {/* Left connecting line */}
            <div className="relative w-1/2">
              <div className="absolute inset-0 flex items-center">
                <div className="h-0.5 w-full bg-gradient-to-r from-indigo-500 to-blue-500"></div>
              </div>
            </div>

            {/* Flight Details */}
            <div className="inline-flex items-center justify-center p-2 bg-blue-50 rounded-full space-x-2 w-1/3">
              <i className="fa-solid fa-plane text-blue-600 text-xs"></i>
              <span className="text-xs font-medium text-blue-600">
                {formatDuration(
                  relevantAddedFlight.legs.reduce(
                    (total, leg) => total + leg.duration,
                    0
                  )
                )}
              </span>
            </div>

            {/* Right connecting line */}
            <div className="relative w-1/2">
              <div className="absolute inset-0 flex items-center">
                <div className="h-0.5 w-full bg-gradient-to-r from-blue-500 to-indigo-500"></div>
              </div>
            </div>

            {/* Arrival */}
            <div className="w-[6.5rem] text-right pl-4">
              <div className="text-base font-bold text-neutral-900">
                {DateTime.fromISO(
                  relevantAddedFlight.legs[relevantAddedFlight.legs.length - 1]
                    ?.arrival.time.utc || ""
                )
                  .setZone(transportData.locationBelow?.date.time_zone)
                  .toFormat("HH:mm")}
              </div>
              <div className="text-xs font-medium text-neutral-900 truncate">
                {relevantAddedFlight.legs[relevantAddedFlight.legs.length - 1]
                  ?.arrival.location.name || "N/A"}
              </div>
              {relevantAddedFlight.legs[relevantAddedFlight.legs.length - 1]
                ?.arrival.location.code && (
                <div className="text-[10px] text-neutral-500 truncate">
                  {relevantAddedFlight.legs[relevantAddedFlight.legs.length - 1]
                    ?.arrival.location.code || "N/A"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Flight Information */}
        <div className="flex gap-2 mt-2">
          <div className="w-1/3 bg-neutral-100 transition-colors rounded-lg px-4 py-2.5 flex items-center justify-center">
            <div className="flex items-center gap-2 text-xs text-neutral-600">
              <i className="fa-regular fa-plane"></i>
              <span className="font-medium truncate max-w-[150px]">
                {relevantAddedFlight.legs.length > 0
                  ? relevantAddedFlight.legs
                      .slice(0, 2)
                      .map((leg) => leg.provider.name)
                      .join(", ") +
                    (relevantAddedFlight.legs.length > 2
                      ? ` + ${relevantAddedFlight.legs.length - 2} more`
                      : "")
                  : "Unknown Airline"}
              </span>
            </div>
          </div>
          <div className="w-1/3 bg-neutral-100 transition-colors rounded-lg px-4 py-2.5 flex items-center justify-center">
            <div className="flex items-center gap-2 text-xs text-neutral-600">
              <i className="fa-regular fa-credit-card"></i>
              <span className="font-medium">
                {formatPrice(
                  relevantAddedFlight.currency,
                  relevantAddedFlight.total_price
                )}{" "}
                / person
              </span>
            </div>
          </div>
          {relevantAddedFlight.type === "api" && (
            <button
              onClick={handleBookNow}
              className={`w-1/3 ${
                relevantAddedFlight.bookingStatus === "booked"
                  ? "bg-indigo-50/50 text-indigo-300 cursor-not-allowed ring-indigo-300"
                  : "bg-indigo-50 hover:bg-indigo-100 text-indigo-500 ring-indigo-500"
              } ring-1 px-4 py-2.5 rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-colors`}
              disabled={relevantAddedFlight.bookingStatus === "booked"}
            >
              <span>Book Now</span>
              <i className="fa-regular fa-arrow-right"></i>
            </button>
          )}
        </div>
      </div>

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        type="transport"
        tripId={tripData?.id}
        transportData={relevantAddedFlight}
        onBooked={handleProviderBooked}
      />

      <Dialog
        open={showBookingConfirmation}
        onOpenChange={setShowBookingConfirmation}
      >
        <DialogContent className="bg-white rounded-xl">
          <DialogHeader>
            <DialogTitle>Did you book your flight?</DialogTitle>
            <DialogDescription className="space-y-2">
              <p className="text-sm text-neutral-600 font-light py-2 pr-2">
                Update your booking status for your flight
              </p>
              <div className="p-3 rounded-xl bg-neutral-50 border border-neutral-200 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center">
                    {renderAirlineAvatars()}
                  </div>
                  <span className="text-sm font-medium text-neutral-900">
                    {renderAirlineNames()}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-neutral-500 mb-0.5 font-light">
                      From
                    </div>
                    <div className="text-sm font-medium text-neutral-900">
                      {relevantAddedFlight.legs[0]?.departure.location.name}
                    </div>
                  </div>
                  <ArrowRightCircleIcon className="h-5 w-5 text-neutral-400 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-xs text-neutral-500 mb-0.5 font-light">
                      To
                    </div>
                    <div className="text-sm font-medium text-neutral-900">
                      {
                        relevantAddedFlight.legs[
                          relevantAddedFlight.legs.length - 1
                        ]?.arrival.location.name
                      }
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-500 font-light">
                  <ClockIcon className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                  <span>
                    {formatDuration(
                      relevantAddedFlight.legs.reduce(
                        (total, leg) => total + leg.duration,
                        0
                      )
                    )}
                  </span>
                  <span className="w-1 h-1 bg-neutral-300 rounded-full"></span>
                  <span>
                    {relevantAddedFlight.legs.length === 1
                      ? "Direct"
                      : `${relevantAddedFlight.legs.length - 1} Stop${
                          relevantAddedFlight.legs.length > 2 ? "s" : ""
                        }`}
                  </span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBookingConfirmation(false)}
              className="w-full sm:w-auto rounded-full font-light text-sm hover:bg-neutral-100"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Not Yet
            </Button>
            <Button
              onClick={handleConfirmBooking}
              className="w-full sm:w-auto bg-green-500/10 border border-green-500/50 text-green-700 hover:bg-green-500/20 rounded-full font-light text-sm"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Yes, I booked it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserFlight;
